/**
 * Entry point: wires the menu to a match, and tears the match back down again.
 */

import './styles.css';
import { MatchRuntime, loadSim, skirmishSettings } from './game/match.js';
import { Menu } from './ui/menu.js';
import { LocalTransport } from './net/local.js';
import { WsTransport } from './net/ws.js';
import type { AiOptions } from './game/ai.js';

const app = document.getElementById('app') as HTMLElement;
const canvas = document.getElementById('view') as HTMLCanvasElement;

let match: MatchRuntime | null = null;
let ws: WsTransport | null = null;

const menu = new Menu(app, {
  onSkirmish: (ai) => void startSkirmish(ai),
  onConnect: (url, room, name) => connect(url, room, name),
});

async function startSkirmish(ai: AiOptions): Promise<void> {
  const settings = skirmishSettings();
  const transport = new LocalTransport(settings);
  await run(transport, { options: ai, players: [1] });
}

function connect(url: string, room: string, name: string): void {
  ws?.close();
  menu.showLobby({ phase: 'connecting' });

  const transport = new WsTransport(url, room, name);
  ws = transport;
  menu.setLobbyHandler((ready) => transport.setReady(ready));
  transport.onLobby((state) => menu.showLobby(state));
  transport.onStart(() => {
    void run(transport, null);
  });
}

async function run(
  transport: LocalTransport | WsTransport,
  ai: { options: AiOptions; players: number[] } | null,
): Promise<void> {
  menu.hide();
  showLoading(true);

  try {
    await loadSim();
    match = new MatchRuntime({
      container: app,
      canvas,
      transport,
      ai,
      onExit: () => {
        match = null;
        menu.show();
      },
    });
    await match.start();
  } catch (err) {
    showError(err);
    menu.show();
  } finally {
    showLoading(false);
  }
}

function showLoading(on: boolean): void {
  document.getElementById('loading')?.classList.toggle('hidden', !on);
}

function showError(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  const box = document.createElement('div');
  box.className = 'fatal';
  box.innerHTML = `<h2>Could not start the match</h2><pre></pre>`;
  box.querySelector('pre')!.textContent = message;
  const dismiss = document.createElement('button');
  dismiss.textContent = 'Back to menu';
  dismiss.className = 'primary';
  dismiss.addEventListener('click', () => box.remove());
  box.appendChild(dismiss);
  app.appendChild(box);
  console.error(err);
}

// Leaving the match with Escape from the banner state.
window.addEventListener('keydown', (e) => {
  if (e.code === 'F10' && match) {
    match.stop();
  }
});
